import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { listUnits } from '../../api/hierarchy-client';
import { UnitListItem } from '../../interfaces/hierarchy-interfaces';
import { palette, radius, spacing, shadow, transition, typography, zIndex } from '../../style-utils/theme';
import { Presence, m, overlayMotion } from '../../motion';

// ---------------------------------------------------------------------------
// UnitSelect — the apartment number, as a dropdown of real units.
// ---------------------------------------------------------------------------
//
// "Apartment/Location" has always been free text, while daily_log and
// visitor_log have carried a nullable unit_id the whole time and both routers
// already validated it (resolveUnitId). So this is not a new field — it is the
// existing FK finally being populated by the form instead of left null.
//
// Why that matters beyond tidiness: the unit is the INPUT to autocapture. A
// typed "4B" narrows nothing, but a chosen unit narrows the resident picker to
// that unit's occupants, which is usually one person — and then nobody has to
// pick anything at all.
//
// ============ FREE TEXT IS STILL ALLOWED ============
// The desk logs deliveries for units that are not in the inventory yet, and for
// places that are not units ("loading bay", "roof"). Forcing a dropdown-only
// choice would make the busiest form in the product unusable the first time
// reality disagreed with the unit list. So: the dropdown fills unit_id AND the
// label; typing something with no match fills the label alone and leaves
// unit_id null, exactly as today.

const Wrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  width: 100%;
`;

const Label = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  transition: ${transition.colors};

  &::placeholder {
    color: ${palette.slateLight};
  }
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

const Menu = m.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  transform-origin: top center;
  /* zIndex.dropdown, not a raw 40 (WP-18). No paint failure was observed
     here — these menus' hosts carry no sticky bar to lose to — so this is
     contract hygiene, not a repro. It matters anyway: 40 sits between no
     two tiers on the scale, so the next sticky element added above this
     host wins silently and the bug looks like a rendering mystery.
     NO BACKTICKS IN THIS COMMENT: it is inside a styled template literal. */
  z-index: ${zIndex.dropdown};
  margin-top: ${spacing.half};
  max-height: 264px;
  overflow-y: auto;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  box-shadow: ${shadow.md};
`;

const Row = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing.xs};
  width: 100%;
  padding: ${spacing.xs} ${spacing.sm};
  border: none;
  background: ${p => (p.active ? palette.irisTintSoft : 'transparent')};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  text-align: left;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

const Occupants = styled.span<{ vacant?: boolean }>`
  color: ${p => (p.vacant ? palette.slateLight : palette.lagoon)};
  font-size: ${typography.size.xxs}px;
  white-space: nowrap;
`;

const Hint = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

export interface UnitSelectProps {
  label?: string;
  /** The free-text label — what the desk typed or the unit they chose. */
  value: string;
  onChange: (label: string, unitId: number | null) => void;
  buildingId?: number | null;
  unitId?: number | null;
  placeholder?: string;
  disabled?: boolean;
}

const norm = (s: string) =>
  String(s || '')
    .trim()
    .replace(/\s/g, '')
    .toUpperCase();

export const UnitSelect: React.FC<UnitSelectProps> = ({
  label = 'Apartment / Location',
  value,
  onChange,
  buildingId,
  unitId,
  placeholder,
  disabled,
}) => {
  const [units, setUnits] = useState<UnitListItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!buildingId) {
      setUnits([]);

      return;
    }
    let live = true;
    setLoading(true);
    listUnits(buildingId)
      .then(rows => {
        if (live) setUnits(rows);
      })
      .catch(() => {
        // The dropdown is an accelerator, not a gate. If the inventory cannot
        // be read the field degrades to the free text it has always been.
        if (live) setUnits([]);
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, [buildingId]);

  const matches = useMemo(() => {
    const q = norm(value);
    const pool = units.filter(u => u.kind !== 'unassigned');
    if (!q) return pool.slice(0, 50);

    return pool.filter(u => norm(u.unit_number).startsWith(q)).slice(0, 50);
  }, [units, value]);

  const choose = (u: UnitListItem) => {
    onChange(u.unit_number, u.id);
    setOpen(false);
  };

  const typed = (next: string) => {
    // Typing after a choice unsets the link unless it still matches exactly —
    // otherwise the label and the FK would silently disagree, which is the
    // self-contradictory row resolveUnitId exists to reject.
    const exact = units.find(u => norm(u.unit_number) === norm(next));
    onChange(next, exact ? exact.id : null);
  };
  const fieldId = `unit-select${React.useId()}`;


  return (
    <Wrap
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      {/* htmlFor + id. The label text is supplied by the caller and the same
          component renders on several steps, so the id is generated rather than
          derived from the words — two "Apartment / Location" pickers on one
          screen would otherwise share one id. */}
      <Label htmlFor={fieldId}>{label}</Label>
      <Input
        id={fieldId}
        type="text"
        value={value}
        maxLength={40}
        placeholder={placeholder || 'Choose or type an apartment'}
        disabled={disabled}
        onChange={e => typed(e.target.value)}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {!!buildingId && !unitId && value.trim() !== '' && !loading && (
        <Hint>Not in the unit list — saved as free text.</Hint>
      )}
      <Presence>
        {open && matches.length > 0 ? (
        <Menu key="unit-options" {...overlayMotion('popover')} role="listbox">
          {matches.map(u => (
            <Row
              key={u.id}
              type="button"
              /* See person-field.tsx: a listbox needs options, and the selected
                 state has to be announced and not only drawn. */
              role="option"
              aria-selected={u.id === unitId}
              active={u.id === unitId}
              onClick={() => choose(u)}
            >
              <span>{u.unit_number}</span>
              {/* Occupancy count, because the desk's next question is always
                  "who lives there" — and a unit with one resident is the case
                  autocapture resolves with no further input. */}
              <Occupants vacant={u.is_vacant}>
                {u.is_vacant
                  ? 'vacant'
                  : `${u.occupant_count} resident${u.occupant_count === 1 ? '' : 's'}`}
              </Occupants>
            </Row>
          ))}
        </Menu>
        ) : null}
      </Presence>
    </Wrap>
  );
};
